import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";

const CLOSE_EVENT = "reconcile-dropdown-close-others";

const ReconcileDropdownMenu = ({ matchStatus, filteredIndex, onMenuClick, sdIgnore, enIgnore }) => {
   const [isOpen, setIsOpen] = useState(false);
   const buttonRef = useRef(null);
   const menuRef = useRef(null);
   const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
   const menuOptions = {
      MATCHED: [
         { label: "Audit", action: "audit", disabled: false },
         { label: "Edit", action: "edit", disabled: false },
         { label: sdIgnore ? "Undo-Supplier Ignore" : "Ignore Supplier", action: sdIgnore ? "undo_ignore_supplier" : "ignore_supplier", disabled: false },
         { label: enIgnore ? "Undo-Ignore Invoice" : "Ignore Invoice", action: enIgnore ? "undo_ignore_invoice" : "ignore_invoice", disabled: false },
         { label: "View Invoice", action: "view_invoice", disabled: false },
      ],
      FULL_MATCH: [
         { label: "Audit", action: "audit", disabled: false },
         { label: "Edit", action: "edit", disabled: false },
         { label: sdIgnore ? "Undo-Supplier Ignore" : "Ignore Supplier", action: sdIgnore ? "undo_ignore_supplier" : "ignore_supplier", disabled: false },
         { label: enIgnore ? "Undo-Ignore Invoice" : "Ignore Invoice", action: enIgnore ? "undo_ignore_invoice" : "ignore_invoice", disabled: false },
         { label: "View Invoice", action: "view_invoice", disabled: false },
      ],
      PARTIAL: [
         { label: "Edit", action: "edit", disabled: false },
         { label: "View Invoice", action: "view_invoice", disabled: false },
         { label: sdIgnore ? "Undo-Supplier Ignore" : "Ignore Supplier", action: sdIgnore ? "undo_ignore_supplier" : "ignore_supplier", disabled: false },
         { label: enIgnore ? "Undo-Ignore Invoice" : "Ignore Invoice", action: enIgnore ? "undo_ignore_invoice" : "ignore_invoice", disabled: true },
         { label: "Audit", action: "audit", disabled: false },
      ],
      UNMATCHED: [
         { label: sdIgnore ? "Undo-Supplier Ignore" : "Ignore Supplier", action: sdIgnore ? "undo_ignore_supplier" : "ignore_supplier", disabled: false },
         { label: "Edit", action: "edit", disabled: false },
         { label: enIgnore ? "Undo-Ignore Invoice" : "Ignore Invoice", action: enIgnore ? "undo_ignore_invoice" : "ignore_invoice", disabled: true },
         { label: "View Invoice", action: "view_invoice", disabled: false },
         { label: "Audit", action: "audit", disabled: false },
      ],
      NO_ERP: [
         { label: sdIgnore ? "Undo-Supplier Ignore" : "Ignore Supplier", action: sdIgnore ? "undo_ignore_supplier" : "ignore_supplier", disabled: false },
         { label: "Edit", action: "edit", disabled: false },
         { label: enIgnore ? "Undo-Ignore Invoice" : "Ignore Invoice", action: enIgnore ? "undo_ignore_invoice" : "ignore_invoice", disabled: true },
         { label: "View Invoice", action: "view_invoice", disabled: false },
         { label: "Audit", action: "audit", disabled: false },
      ],
   };

   const normalizedStatus = String(matchStatus || "").toUpperCase();
   const options = menuOptions[normalizedStatus] || [
      { label: "Edit", action: "edit", disabled: false },
      { label: "View Invoice", action: "view_invoice", disabled: false },
      { label: "Audit", action: "audit", disabled: false },
   ];
   const dropdownId = `reconcile-actions-dropdown-${filteredIndex ?? "row"}`;

   const updateMenuPosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = menuRef.current?.offsetWidth || 190;
      const maxLeft = Math.max(8, window.innerWidth - menuWidth - 8);
      const left = Math.min(Math.max(8, rect.right - menuWidth), maxLeft);
      setMenuPos({ top: rect.bottom + 4, left });
   };

   useEffect(() => {
      const handleCloseOthers = (e) => {
         if (e.detail !== dropdownId) setIsOpen(false);
      };
      document.addEventListener(CLOSE_EVENT, handleCloseOthers);
      return () => document.removeEventListener(CLOSE_EVENT, handleCloseOthers);
   }, [dropdownId]);

   useEffect(() => {
      if (!isOpen) return;
      const rafId = requestAnimationFrame(updateMenuPosition);
      const onViewportChange = () => updateMenuPosition();
      const onOutsideMouseDown = (e) => {
         if (buttonRef.current?.contains(e.target)) return;
         if (menuRef.current?.contains(e.target)) return;
         setIsOpen(false);
      };
      const onEscape = (e) => {
         if (e.key === "Escape") setIsOpen(false);
      };
      window.addEventListener("resize", onViewportChange);
      window.addEventListener("scroll", onViewportChange, true);
      document.addEventListener("mousedown", onOutsideMouseDown);
      document.addEventListener("keydown", onEscape);
      return () => {
         cancelAnimationFrame(rafId);
         window.removeEventListener("resize", onViewportChange);
         window.removeEventListener("scroll", onViewportChange, true);
         document.removeEventListener("mousedown", onOutsideMouseDown);
         document.removeEventListener("keydown", onEscape);
      };
   }, [isOpen]);

   return (
      <div
         className="reconcile-actions-area"
         style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
         }}>
         <div
            style={{
               borderLeft: "1px solid #ccc",
               height: "16px",
            }}></div>
         <div>
            <button
               ref={buttonRef}
               type="button"
               className="btn btn-link p-0 border-0"
               id={dropdownId}
               aria-expanded={isOpen}
               onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  document.dispatchEvent(new CustomEvent(CLOSE_EVENT, { detail: dropdownId }));
                  if (!isOpen) {
                     const rect = e.currentTarget.getBoundingClientRect();
                     const estimatedMenuWidth = menuRef.current?.offsetWidth || 190;
                     const maxLeft = Math.max(8, window.innerWidth - estimatedMenuWidth - 8);
                     const left = Math.min(Math.max(8, rect.right - estimatedMenuWidth), maxLeft);
                     setMenuPos({ top: rect.bottom + 4, left });
                  }
                  setIsOpen((prev) => !prev);
               }}
               onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
               }}
               style={{ cursor: "pointer", boxShadow: "none" }}>
               <FaChevronDown />
            </button>
            {isOpen &&
               createPortal(
                  <ul
                     ref={menuRef}
                     className="dropdown-menu show"
                     aria-labelledby={dropdownId}
                     style={{
                        position: "fixed",
                        top: `${menuPos.top}px`,
                        left: `${menuPos.left}px`,
                        display: "block",
                        zIndex: 3000,
                        minWidth: "190px",
                     }}
                     onMouseDown={(e) => e.stopPropagation()}
                     onClick={(e) => e.stopPropagation()}>
                     {options.map((item, index) => (
                        <li key={`ml-${index}`}>
                           <button
                              type="button"
                              className="dropdown-item"
                              disabled={item.disabled}
                              onClick={(e) => {
                                 e.preventDefault();
                                 e.stopPropagation();
                                 setIsOpen(false);
                                 onMenuClick(item.action, filteredIndex);
                              }}>
                              {item.label}
                           </button>
                        </li>
                     ))}
                  </ul>,
                  document.body
               )}
         </div>
      </div>
   );
};

export default ReconcileDropdownMenu;
